import React from 'react';
import dayjs from 'dayjs';
import { TReview } from 'types/inbox';
import { getTimeStr } from 'utils/calendar';
import { StarFilled } from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import s from './s.module.less';
import ReplyReviewModal from 'pages/dashboard/PPAKlarityReviewsDashboradPage/components/ReplyReviewModal';
import ShareReviewModal from './ShareReviewModal';
import { TReviewItem, EReviewSource, TReviewComment } from 'pages/dashboard/PPAKlarityReviewsDashboradPage/types';
import { sanitizeRichHtml } from 'utils/sanitize';

type Props = {
    review: TReview;
    createdAt: string;
    showSendTime?: boolean;
    onReplySuccess?: () => void;
};

const ReviewMessage: React.FC<Props> = ({ review, createdAt, showSendTime, onReplySuccess }) => {
    // 弹窗状态管理
    const [showReplyModal, setShowReplyModal] = React.useState(false);
    const [showShareModal, setShowShareModal] = React.useState(false);
    // 回复内容展开/收起状态
    const [showReplyContent, setShowReplyContent] = React.useState(false);

    // 将 TReview 转换为 TReviewItem 格式
    const convertToReviewItem = (reviewData: TReview): TReviewItem => {
        // 转换 source 字符串为 EReviewSource 枚举
        let source: EReviewSource | undefined;
        if (reviewData.source === 'EHR_BOOKING') {
            source = EReviewSource.EHR_BOOKING;
        } else if (reviewData.source === 'SURVEY_LINK') {
            source = EReviewSource.SURVEY_LINK;
        } else if (reviewData.source === 'PROVIDER_IMPORT') {
            source = EReviewSource.PROVIDER_IMPORT;
        }

        // 构建 comment 对象
        let comment: TReviewComment | undefined;
        if (reviewData.reviewComment) {
            comment = {
                id: reviewData.commentId,
                commentMemberId: 0,
                commentMemberType: '',
                rootId: reviewData.commentId,
                likeCount: 0,
                isPublic: true,
                isSticky: false,
                isAnonymous: false,
                isApprove: true,
                content: reviewData.reviewComment,
                createAt: reviewData.submitAt,
                createBy: 0,
                updateAt: reviewData.submitAt,
                isDeleted: false,
            };
        }

        return {
            id: reviewData.id,
            uuid: reviewData.uuid,
            bpProviderId: reviewData.bpProviderId,
            intakeqProviderId: '',
            providerSource: reviewData.providerSource,
            commentId: reviewData.commentId,
            rating: reviewData.rating,
            tags: reviewData.tags || '',
            isPublic: reviewData.isPublic || false,
            isAnonymous: false,
            source,
            sourceId: reviewData.sourceId,
            reviewType: reviewData.appointmentServiceType || 'INITIAL_VISIT',
            contentLength: reviewData.contentLength,
            isReplied: !!reviewData.repliedAt,
            repliedAt: reviewData.repliedAt || undefined,
            // 后端可能返回 undefined，这里强制转换为布尔值，避免类型不匹配
            isNeedReplied: !!reviewData.isNeedReplied, // 如果没有回复过，则需要回复
            isApprove: true,
            reviewStatus: reviewData.reviewStatus as 'S' | 'I',
            submitAt: reviewData.submitAt,
            comment,
            reviewId: reviewData.id,
            reviewerEmail: reviewData.reviewerEmail,
            reviewerPhone: reviewData.reviewerPhone || null,
        };
    };
    // 解析标签
    const tags = review.tags ? review.tags.split(',').map((tag) => tag.trim()).filter(Boolean) : [];
    // 格式化预约时间
    const formatAppointmentTime = () => {
        if (!review.appointmentStartAt) return '';
        const startTime = dayjs(review.appointmentStartAt);
        const endTime = startTime.add(review.appointmentDuration || 0, 'minute');
        const timeStr = `${getTimeStr(startTime, 'hh:mm A')} - ${getTimeStr(endTime, 'hh:mm A')}`;
        const duration = review.appointmentDuration ? `${review.appointmentDuration} mins` : '';
        return `${timeStr} | ${duration}`;
    };

    // 格式化预约日期
    const formatAppointmentDate = () => {
        if (!review.appointmentStartAt) return '';
        return getTimeStr(dayjs(review.appointmentStartAt), 'dddd, MMMM D, YYYY');
    };

    // 获取患者姓名：新注册的患者可能还没有姓名，降级为通用文案，避免展示占位符
    const patientName = review.patientFirstName?.trim() || review.reviewerName?.trim().split(' ')[0] || 'a verified patient';

    // 判断是否已回复
    const isReplied = review.isReplied ?? !!review.repliedAt;

    // 判断是否需要回复（如果没有回复过，则需要回复）
    const { isNeedReplied } = review;

    // 格式化回复日期
    const formatRepliedDate = () => {
        if (!review.repliedAt) return '';
        return getTimeStr(dayjs(review.repliedAt), 'MMM D, YYYY');
    };

    // 判断是否超过30天回复期限
    const isReplyPeriodExpired = () => {
        if (!review.submitAt) return false;
        const submitDate = dayjs(review.submitAt);
        const now = dayjs();
        const daysDiff = now.diff(submitDate, 'day');
        return daysDiff > 30;
    };

    const replyPeriodExpired = isReplyPeriodExpired();

    return (
        <div className={s.reviewItem}>
            {!!showSendTime && (
                <div className={s.sendTimeWrap}>
                    System
                    <span className={s.sendTimeVal}>
                        {getTimeStr(dayjs(createdAt), 'hh:mm A')}
                    </span>
                </div>
            )}
            <div className={s.reviewWrap}>
                <div className={s.reviewContent}>
                    <p className={s.reviewGreeting}>
                        Congratulations! You&apos;ve received a positive review
                        from {patientName} about their recent appointment.
                    </p>

                    <div className={s.reviewDivider} />

                    <div className={s.reviewRatingsSection}>
                        <div className={s.reviewRatingsRow}>
                            <div className={s.reviewRatingsLeft}>
                                <span className={s.reviewRatingsLabel}>
                                    Ratings:
                                </span>
                                <div className={s.reviewStars}>
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <StarFilled
                                            key={star}
                                            className={s.reviewStar}
                                            style={{
                                                color:
                                                    star <= (review.rating || 0)
                                                        ? '#FFCB45'
                                                        : '#E5E7EB',
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                            <div
                                className={
                                    review.isPublic
                                        ? s.reviewPublicBadge
                                        : s.reviewPrivateBadge
                                }
                            >
                                {review.isPublic ? 'Public' : 'Private'}
                            </div>
                        </div>

                        {tags.length > 0 && (
                            <div className={s.reviewTags}>
                                {tags.map((tag, index) => (
                                    <span key={index} className={s.reviewTag}>
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {review.reviewComment && (
                        <>
                            <div className={s.reviewDivider} />
                            <div className={s.reviewCommentSection}>
                                <span className={s.reviewCommentLabel}>
                                    Reviews:
                                </span>
                                <span className={s.reviewCommentText}>
                                    {' '}
                                    &quot;{review.reviewComment}&quot;
                                </span>
                            </div>
                        </>
                    )}

                    {(review.appointmentName || review.appointmentStartAt) && (
                        <>
                            <div className={s.reviewDivider} />
                            <div className={s.reviewAppointmentSection}>
                                <p className={s.reviewAppointmentLabel}>
                                    Appointment details:
                                </p>
                                <ul className={s.reviewAppointmentList}>
                                    {review.appointmentName && (
                                        <li>{review.appointmentName}</li>
                                    )}
                                    {review.appointmentStartAt && (
                                        <li>Date: {formatAppointmentDate()}</li>
                                    )}
                                    {review.appointmentStartAt && (
                                        <li>Time: {formatAppointmentTime()}</li>
                                    )}
                                </ul>
                            </div>
                        </>
                    )}

                    <div className={s.reviewDivider} />

                    <p className={s.reviewFooter}>
                        Keep building trust by sharing this review on social
                        media or replying directly to show your appreciation.
                    </p>
                </div>
            </div>

            {/* 回复信息区域 */}
            {isReplied && review.repliedAt && (
                <div className={s.replyInfoSection}>
                    <div className={s.replyInfoHeader}>
                        <span className={s.replyDateText}>
                            Replied on {formatRepliedDate()}
                        </span>
                        <span
                            className={s.replyToggleLink}
                            onClick={() => {
                                setShowReplyContent(!showReplyContent);
                            }}
                        >
                            {showReplyContent ? 'Hide response' : 'Show response'}
                        </span>
                    </div>
                    {showReplyContent && (
                        <p className={s.replyContentText} dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(review.repliedContent || '-') }} />
                    )}
                </div>
            )}

            <div className={s.reviewActions}>
                {review.isPublic && (
                    <Button
                        className={s.reviewActionButton}
                        onClick={() => {
                            setShowShareModal(true);
                        }}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                        >
                            <path
                                d="M6.4 0C5.95817 0 5.6 0.358172 5.6 0.8C5.6 1.24183 5.95817 1.6 6.4 1.6H8.46863L3.43431 6.63432C3.1219 6.94673 3.1219 7.45327 3.43431 7.76569C3.74673 8.07811 4.25327 8.07811 4.56569 7.76569L9.6 2.73137V4.8C9.6 5.24183 9.95817 5.6 10.4 5.6C10.8418 5.6 11.2 5.24183 11.2 4.8V0.8C11.2 0.358172 10.8418 0 10.4 0H6.4Z"
                                fill="#6B7280"
                            />
                            <path
                                d="M1.6 1.6C0.716345 1.6 0 2.31634 0 3.2V9.6C0 10.4837 0.716344 11.2 1.6 11.2H8C8.88366 11.2 9.6 10.4837 9.6 9.6V7.2C9.6 6.75817 9.24183 6.4 8.8 6.4C8.35817 6.4 8 6.75817 8 7.2V9.6H1.6V3.2L4 3.2C4.44183 3.2 4.8 2.84183 4.8 2.4C4.8 1.95817 4.44183 1.6 4 1.6H1.6Z"
                                fill="#6B7280"
                            />
                        </svg>
                        Share to social media
                    </Button>
                )}
                {(() => {
                    if (isReplied) {
                        return (
                            <Button className={`${s.reviewActionButton} ${s.reviewActionButtonReplied}`} disabled>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                >
                                    <path
                                        d="M2 6.66602H8.66667C11.6122 6.66602 14 9.05383 14 11.9993V13.3327M2 6.66602L6 10.666M2 6.66602L6 2.66602"
                                        stroke="#9CA3AF"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                Replied
                            </Button>
                        );
                    }
                    // 只有当 isNeedReplied 为 true 时才显示回复按钮
                    if (!isNeedReplied) {
                        return null;
                    }
                    if (replyPeriodExpired) {
                        return (
                            <Tooltip title="The 30-day reply period has expired" placement="top">
                                <Button
                                    className={`${s.reviewActionButton} ${s.reviewActionButtonExpired}`}
                                    disabled
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                    >
                                        <path
                                            d="M2 6.66602H8.66667C11.6122 6.66602 14 9.05383 14 11.9993V13.3327M2 6.66602L6 10.666M2 6.66602L6 2.66602"
                                            stroke="#9CA3AF"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    Reply to patient
                                </Button>
                            </Tooltip>
                        );
                    }
                    return (
                        <Button
                            className={s.reviewActionButton}
                            onClick={() => {
                                setShowReplyModal(true);
                            }}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                            >
                                <path
                                    d="M2 6.66602H8.66667C11.6122 6.66602 14 9.05383 14 11.9993V13.3327M2 6.66602L6 10.666M2 6.66602L6 2.66602"
                                    stroke="#6B7280"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                            Reply to patient
                        </Button>
                    );
                })()}
            </div>
            <ReplyReviewModal
                show={showReplyModal}
                item={convertToReviewItem(review)}
                onSuccess={() => {
                    // 关闭弹窗并触发外层刷新消息列表
                    setShowReplyModal(false);
                    if (onReplySuccess) {
                        onReplySuccess();
                    }
                }}
                onCannel={() => {
                    setShowReplyModal(false);
                }}
            />
            <ShareReviewModal
                visible={showShareModal}
                review={review}
                onCancel={() => {
                    setShowShareModal(false);
                }}
            />
        </div>
    );
};

export default ReviewMessage;
